import { supabase } from './supabase'
import { COMMUNITY_USER_USERNAME } from './constants'
import { founderAsksService } from './founderAsks'

// Data for the Create panel previews (design ref: Community Asks.dc.html §8a).
// All previews are scoped to the Purpose Collective house account — resolved by
// username, never a hard-coded ID — so imagery is always on-brand.
//
// TODO(api): the design spec sketches GET /create-panel/previews as one
// cacheable endpoint. This client-side loader is shape-identical; swap the
// internals for that endpoint if/when it exists.

export interface CreatePanelPreviews {
  // card: the full contribution row (+ author) so the panel can render the real
  // discover-feed ItemCard; imageUrl/alt remain for the sheet thumbnail.
  latestItem: { imageUrl: string; alt: string; card: Record<string, any> } | null
  latestCuration: { items: { imageUrl: string; alt: string }[] } | null
  founders: { name: string; avatarUrl: string | null; username: string | null }[]
}

let cache: Promise<CreatePanelPreviews> | null = null

// One fetch per session ("cacheable for hours") shared by panel and sheet.
export function fetchCreatePanelPreviews(): Promise<CreatePanelPreviews> {
  if (!cache) {
    cache = load().catch(err => {
      cache = null
      throw err
    })
  }
  return cache
}

async function load(): Promise<CreatePanelPreviews> {
  const { data: house } = await supabase
    .from('users')
    .select('id')
    .eq('username', COMMUNITY_USER_USERNAME)
    .single()

  const [item, curation, founders] = await Promise.all([
    house ? loadLatestItem(house.id) : Promise.resolve(null),
    house ? loadLatestCuration(house.id) : Promise.resolve(null),
    founderAsksService.fetchFounders().catch(() => []),
  ])

  return {
    latestItem: item,
    latestCuration: curation,
    founders: founders.map(f => ({
      name: f.first_name,
      avatarUrl: f.avatar_url,
      username: f.username,
    })),
  }
}

async function loadLatestItem(houseId: string): Promise<CreatePanelPreviews['latestItem']> {
  const { data } = await supabase
    .from('contributions')
    .select(`
      id, title, brand, cached_image_url, image_url, source_url, comment, created_at, saves_count,
      user:users!user_id(id, username, first_name, last_name, avatar_url)
    `)
    .eq('user_id', houseId)
    .eq('is_active', true)
    .order('created_at', { ascending: false })
    .limit(1)

  const row = data?.[0] as any
  const imageUrl = row?.cached_image_url || row?.image_url
  if (!imageUrl) return null
  const user = Array.isArray(row.user) ? row.user[0] : row.user
  return { imageUrl, alt: row.title || 'Latest shared item', card: { ...row, user } }
}

async function loadLatestCuration(houseId: string): Promise<CreatePanelPreviews['latestCuration']> {
  const { data } = await supabase
    .from('issues')
    .select('id, title, issue_items(position, contributions(title, cached_image_url, image_url))')
    .eq('curator_id', houseId)
    .eq('status', 'published')
    .order('published_at', { ascending: false })
    .limit(1)

  const issue = data?.[0] as any
  if (!issue?.issue_items?.length) return null

  const items = issue.issue_items
    .slice()
    .sort((a: any, b: any) => a.position - b.position)
    .map((it: any) => {
      const c = Array.isArray(it.contributions) ? it.contributions[0] : it.contributions
      const imageUrl = c?.cached_image_url || c?.image_url
      return imageUrl ? { imageUrl, alt: c?.title || issue.title || 'Curation item' } : null
    })
    .filter(Boolean)
    .slice(0, 4) as { imageUrl: string; alt: string }[]

  return items.length > 0 ? { items } : null
}
